import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, CheckCircle2, Users, Target, TrendingUp, Shield } from 'lucide-react';

const HowWeWork = () => {
  const principles = [
    {
      icon: <Users size={32} />,
      title: 'Client-Centric Collaboration',
      description: 'We work as an extension of your team, aligning our advisory with your strategic objectives and decision-making timelines.',
    },
    {
      icon: <Target size={32} />,
      title: 'Data-Driven Decisions',
      description: 'Every recommendation is grounded in rigorous analysis, market data, and engineering best practice — no guesswork.',
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Pragmatic Solutions',
      description: 'We balance technical excellence with commercial reality, delivering advice that works in the real world.',
    },
    {
      icon: <Shield size={32} />,
      title: 'Independence & Integrity',
      description: 'Our only interest is your project success. No conflicts, no bias — just honest, expert guidance.',
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Initial Consultation',
      description: 'We begin with a no-obligation discussion to understand your project, objectives, and challenges. This allows us to tailor our approach to your specific needs.',
    },
    {
      step: '02',
      title: 'Scope Definition',
      description: 'We develop a clear scope of services and engagement structure, ensuring alignment on deliverables, timelines, and commercial terms from the outset.',
    },
    {
      step: '03',
      title: 'Data Gathering & Analysis',
      description: 'We conduct thorough research and data collection, leveraging market intelligence, technical standards, and engineering rigour to inform our advisory.',
    },
    {
      step: '04',
      title: 'Strategy Development',
      description: 'We develop pragmatic strategies and recommendations, supported by clear analysis and sensitivity testing to account for key risks and variables.',
    },
    {
      step: '05',
      title: 'Implementation Support',
      description: 'We work alongside you to implement recommendations, providing ongoing advisory and adapting our approach as the project evolves.',
    },
    {
      step: '06',
      title: 'Continuous Review',
      description: 'Throughout engagement, we monitor progress, manage risks, and provide regular reporting to keep stakeholders informed and projects on track.',
    },
  ];

  const engagementModels = [
    {
      title: 'Project-Specific Advisory',
      description: 'Defined scope engagement for a specific project phase or deliverable, such as feasibility study or procurement support.',
    },
    {
      title: 'Retainer Arrangements',
      description: 'Ongoing advisory relationship providing on-call support across multiple projects or development stages.',
    },
    {
      title: 'Strategic Partnership',
      description: 'Long-term collaboration embedded within your development strategy, from portfolio planning through to asset exit.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#0d1b2e] text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-[#4a9ece] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 animate-fadeIn">
              How We Work
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Our approach combines engineering rigour with commercial pragmatism, delivering independent advisory that drives real project value.
            </p>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              Our Core Principles
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The foundations of every client relationship and project engagement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <Card key={index} className="border-slate-200 hover:border-[#4a9ece] hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#4a9ece] text-white rounded-full mb-4">
                    {principle.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-[#0d1b2e] mb-3">
                    {principle.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {principle.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              Our Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A structured yet flexible approach that adapts to your project needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <Card className="border-slate-200 hover:border-[#4a9ece] transition-all duration-300 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-[#0d1b2e] text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                        {item.step}
                      </div>
                      <h3 className="text-xl font-semibold text-[#0d1b2e]">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-slate-600 leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Models */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-4">
              Flexible Engagement Models
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We structure our services to suit your project scale and timeline
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {engagementModels.map((model, index) => (
              <Card key={index} className="border-slate-200 hover:border-[#4a9ece] hover:shadow-lg transition-all duration-300">
                <CardContent className="p-8">
                  <div className="w-8 h-1 bg-[#4a9ece] mb-6" />
                  <h3 className="text-xl font-semibold text-[#0d1b2e] mb-4">
                    {model.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {model.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#0d1b2e] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Work With Us?
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                In a market full of consultants, we differentiate through our commitment to independence, technical rigour, and commercial pragmatism. You get honest advice from an engineer who understands both the technical and business sides of development.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="text-[#4a9ece] flex-shrink-0 mt-0.5" size={24} />
                  <span className="text-slate-200">No conflicts of interest — your success is our only priority</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="text-[#4a9ece] flex-shrink-0 mt-0.5" size={24} />
                  <span className="text-slate-200">Engineering expertise combined with commercial acumen</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="text-[#4a9ece] flex-shrink-0 mt-0.5" size={24} />
                  <span className="text-slate-200">Proven track record across Irish and EU built environment</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="text-[#4a9ece] flex-shrink-0 mt-0.5" size={24} />
                  <span className="text-slate-200">Responsive, adaptable approach tailored to your needs</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1519662978799-2f05096d3636"
                alt="Professional engineering"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2e]/40 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0d1b2e] mb-6">
            Let's Start the Conversation
          </h2>
          <p className="text-xl text-slate-600 mb-8">
            Get in touch to discuss your project and explore how we can support you from concept to completion.
          </p>
          <Link to="/contact">
            <Button className="bg-[#4a9ece] hover:bg-[#3a8ebe] text-white px-10 py-6 text-lg">
              Contact Us Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
